import React, { useState, useEffect } from 'react';
import { getBackendBaseUrl } from '../../config/backendUrl';
import './PopupForm.css';
import imgs1 from "../Assets/stick_1.webp";

function PopupForm({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    purpose: '',
    customPurpose: '', // For "Others" option
    collegeName: '',
    departmentName: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedTopic, setSelectedTopic] = useState('');
  const [audio, setAudio] = useState(null);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [recordingId, setRecordingId] = useState(null);
  const [, setUserLevel] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [stableShuffledQuestions, setStableShuffledQuestions] = useState([]);
  const [sessionId, setSessionId] = useState('');
  // Tracks whether the current user already completed these steps in a
  // previous session, so the quiz screen can hide the parts already done.
  const [existingQuizDone, setExistingQuizDone] = useState(false);
  const [existingAudioDone, setExistingAudioDone] = useState(false);
  const [audioUploadError, setAudioUploadError] = useState('');

  // Generate or load session ID
  useEffect(() => {
    let currentSessionId = localStorage.getItem('quizSessionId');
    if (!currentSessionId) {
      currentSessionId = 'quiz_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('quizSessionId', currentSessionId);
    }
    setSessionId(currentSessionId);
    console.log('Session ID:', currentSessionId);
  }, []);

  // Detect when student returns to the quiz tab and shuffle questions
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && showQuiz && sessionId && !existingQuizDone) {
        console.log('Student returned to tab, shuffling questions to prevent cheating...');
        fetchQuestionsFromBackend();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showQuiz, sessionId, existingQuizDone]);

  // Save questions to localStorage whenever they change
  useEffect(() => {
    if (stableShuffledQuestions.length > 0 && sessionId) {
      console.log('Saving questions to localStorage:', stableShuffledQuestions.length);
      localStorage.setItem(`quizQuestions_${sessionId}`, JSON.stringify(stableShuffledQuestions));
    }
  }, [stableShuffledQuestions, sessionId]);

  // Save answers to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(quizAnswers).length > 0 && sessionId) {
      console.log('Saving answers to localStorage:', Object.keys(quizAnswers).length);
      localStorage.setItem(`quizAnswers_${sessionId}`, JSON.stringify(quizAnswers));
    }
  }, [quizAnswers, sessionId]);

  // Backend API URL - Update this to match your web server setup
  // Google Sheets is no longer called from the browser — the PHP backend
  // (Backend/form.php) writes to Google Sheets itself, only after a
  // successful MySQL write, so the two stay in sync.
  const API_URL = `${getBackendBaseUrl()}/form.php`;

  // Fetch a random audio recording title from the backend (the admin-managed
  // Audio Title list), instead of a hardcoded local topic array.
  useEffect(() => {
    const fetchAudioTitle = async () => {
      try {
        const response = await fetch(`${API_URL}/audio-title`);
        const contentType = response.headers.get('content-type') || '';
        if (!response.ok || !contentType.includes('application/json')) {
          console.error('Audio title request failed:', response.status);
          return;
        }

        const data = await response.json();
        if (data.success && data.title) {
          setSelectedTopic(data.title);
        }
      } catch (error) {
        console.error('Error fetching audio title:', error);
      }
    };
    fetchAudioTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const purposeOptions = [
    { value: '', label: 'Select Purpose of Study' },
    { value: 'Academic Purposes', label: 'Academic Purposes' },
    { value: 'Career & Professional Development', label: 'Career & Professional Development' },
    { value: 'Communication & Travel', label: 'Communication & Travel' },
    { value: 'Personal Development', label: 'Personal Development' },
    { value: 'Test Preparation', label: 'Test Preparation' },
    { value: 'Teaching & Tutoring', label: 'Teaching & Tutoring' },
    { value: 'Others', label: 'Others' }
  ];

  // Fetch questions from backend. The backend already returns them in the
  // required order — 4 beginner, then 4 intermediate, then 4 advanced,
  // then 1 reading-comprehension question — with the specific questions
  // (and their order within each level) randomized per request, so every
  // visitor gets a different quiz. The frontend must NOT re-shuffle this
  // list, or it would scramble that fixed category ordering.
  const fetchQuestionsFromBackend = async () => {
    console.log('Fetching questions from backend...');
    setQuestionsLoading(true);
    try {
      const response = await fetch(`${API_URL}/questions`);
      const data = await response.json();

      if (data.success) {
        console.log('Questions fetched from backend:', data.questions.length);
        setQuestions(data.questions);
        // Clear previous answers when fetching a new set
        setQuizAnswers({});
        setStableShuffledQuestions(data.questions);
      } else {
        console.error('Failed to fetch questions:', data.error);
        // Fallback to empty array if API fails
        setQuestions([]);
        setStableShuffledQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestions([]);
      setStableShuffledQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Fetch questions when quiz starts (skip if this user already answered
  // them in a previous session — they only need to finish the audio step)
  useEffect(() => {
    console.log('Quiz started, showQuiz:', showQuiz, 'stableShuffledQuestions.length:', stableShuffledQuestions.length, 'sessionId:', sessionId);
    if (showQuiz && sessionId && !existingQuizDone) {
      // Always fetch and shuffle new questions when quiz becomes visible
      // This prevents cheating by switching tabs
      console.log('Student returned to quiz, fetching and shuffling new questions...');
      fetchQuestionsFromBackend();
    }
  }, [showQuiz, sessionId, existingQuizDone]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error messages when user starts typing
    if (name === 'email' && emailError) {
      setEmailError('');
    }
    if (name === 'phone' && phoneError) {
      setPhoneError('');
    }
    if (registrationError) {
      setRegistrationError('');
    }
    // Reset customPurpose when purpose is not "Others"
    if (name === 'purpose' && value !== 'Others') {
      setFormData(prev => ({ ...prev, [name]: value, customPurpose: '' }));
    }
  };

  // Submit registration data to backend database
  const submitRegistrationToBackend = async (formData, uniqueId) => {
    try {
      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: uniqueId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
          purpose: formData.purpose,
          custom_purpose: formData.customPurpose,
          college_name: formData.collegeName,
          department_name: formData.departmentName
        })
      });
      
      const responseText = await response.text();
      
      // Clean up response text to remove any HTML error output
      const cleanResponse = responseText.replace(/<[^>]*>/g, '').trim();
      
      let result;
      try {
        result = JSON.parse(cleanResponse);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response text:', responseText);
        // Handle non-JSON response
        if (response.status === 409) {
          // Conflict - likely duplicate email/phone
          console.error('Registration conflict detected');
          // You can show a generic error message to the user
          return { success: false, conflict: true };
        }
        throw new Error('Invalid server response');
      }
      
      if (result.success) {
        console.log('Registration saved to database successfully');
        // Clear any previous errors
        setRegistrationError('');
        setEmailError('');
        setPhoneError('');
        if (result.existing) {
          // This email/phone already registered previously — resume using
          // their existing user_id instead of creating a new one.
          return {
            success: true,
            existing: true,
            user_id: result.user_id,
            quiz_completed: !!result.quiz_completed,
            audio_uploaded: !!result.audio_uploaded
          };
        }
        return { success: true };
      } else {
        console.error('Database registration failed:', result.error);
        // Handle specific error types
        if (result.field === 'email') {
          setEmailError(result.message);
          setRegistrationError('');
          return { success: false, field: 'email', message: result.message };
        } else if (result.field === 'phone') {
          setPhoneError(result.message);
          setRegistrationError('');
          return { success: false, field: 'phone', message: result.message };
        } else {
          setRegistrationError(result.error || 'Registration failed');
          setEmailError('');
          setPhoneError('');
          return { success: false, field: 'general', message: result.error || 'Registration failed' };
        }
      }
    } catch (error) {
      console.error('Error submitting registration to database:', error);
      // Handle network errors or other issues
      if (error.message === 'Invalid server response') {
        console.error('Server returned invalid response format');
      }
      return { success: false, field: 'general', message: 'Network error occurred' };
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const uniqueId = 'ID_' + Date.now() + '_' + Math.floor(Math.random() * 1000);

      // Submit registration to the backend. The backend writes to MySQL and,
      // only if that succeeds, mirrors the registration to Google Sheets itself.
      const registrationResult = await submitRegistrationToBackend(formData, uniqueId);

      // Only proceed to quiz if registration was successful
      if (registrationResult.success) {
        if (registrationResult.existing) {
          // Same email/phone as before but assessment isn't fully complete —
          // resume with their existing user_id and skip the finished steps.
          setRecordingId(registrationResult.user_id);
          setExistingQuizDone(registrationResult.quiz_completed);
          setExistingAudioDone(registrationResult.audio_uploaded);
        } else {
          setRecordingId(uniqueId);
          setExistingQuizDone(false);
          setExistingAudioDone(false);
        }
        // Move to quiz
        setShowQuiz(true);
      } else {
        // Registration failed, stop loading and allow retry
        console.log('Registration failed, showing error for retry');
        // Loading state will be cleared in finally block
      }

    } catch (err) {
      console.error('Form submission error:', err);
      // Still proceed to quiz even if submission fails
      const uniqueId = 'ID_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
      setRecordingId(uniqueId);
      setShowQuiz(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOptionChange = (questionId, value) => {
    setQuizAnswers({ ...quizAnswers, [questionId]: value });
  };

  const handleAudioChange = (e) => {
    setAudio(e.target.files[0]);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Function to determine level based on percentage score, so it stays
  // correct regardless of the exact question count returned by the backend.
  const determineLevel = (score, total) => {
    const percentage = total > 0 ? (parseInt(score, 10) / total) * 100 : 0;
    if (percentage >= 80) return 'Advanced';
    if (percentage >= 53) return 'Intermediate';
    return 'Beginner';
  };

  // Submit quiz results (and, if present, the audio recording) to the backend.
  // The backend saves everything to MySQL and, only on success, mirrors the
  // quiz result (and uploads the audio) to Google Sheets/Drive itself.
  const submitQuizToBackend = async (quizData) => {
    try {
      const response = await fetch(`${API_URL}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quizData)
      });

      const result = await response.json();
      if (result.success) {
        console.log('Quiz submitted to backend successfully');
      } else {
        console.error('Backend submission failed:', result.error);
      }
      return result;
    } catch (error) {
      console.error('Error submitting quiz to backend:', error);
      return { success: false };
    }
  };

  const handleQuizSubmit = async (e) => {
    e.preventDefault();

    // Audio-only mode (questions already answered in a previous session):
    // require the file before allowing submission.
    if (existingQuizDone && !audio) {
      setAudioUploadError('Please select an audio file to upload.');
      return;
    }
    setAudioUploadError('');

    setIsSubmitting(true);

    try {
      let quizData;

      if (existingQuizDone) {
        // Only the audio step is left — resubmitting answers isn't needed.
        quizData = {
          user_id: recordingId,
          email: formData.email,
          name: formData.name,
          audioOnly: true
        };
      } else {
        // Calculate score
        let score = 0;
        stableShuffledQuestions.forEach(q => {
          if (quizAnswers[q.id] === q.correct_answer) {
            score += 1;
          }
        });

        // Determine level based on score
        const level = determineLevel(score, stableShuffledQuestions.length);
        setUserLevel(level);

        quizData = {
          user_id: recordingId,
          email: formData.email,
          name: formData.name,
          answers: quizAnswers,
          score: score.toString(),
          level: level
        };
      }

      // Handle audio upload if present — sent as base64 to the backend,
      // which forwards it to Google Drive via the Apps Script and stores
      // the returned URL back in MySQL.
      if (audio) {
        try {
          const audioBase64 = await fileToBase64(audio);
          quizData.audioBase64 = audioBase64;
          quizData.audioType = audio.type;
          quizData.audioName = audio.name;
          quizData.audioSize = audio.size;
        } catch (audioError) {
          console.error('Audio processing error:', audioError);
        }
      }

      // Submit quiz (and audio, if any) to the backend in a single call
      const result = await submitQuizToBackend(quizData);
      if (result.audio_url) {
        setAudioUrl(result.audio_url);
      }

      setQuizSubmitted(true);

      // Close popup after 3 seconds
      setTimeout(() => {
        onClose();
        // Reset all states
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          purpose: '',
          customPurpose: '',
          collegeName: '',
          departmentName: ''
        });
        setShowQuiz(false);
        setQuizAnswers({});
        setAudio(null);
        setQuizSubmitted(false);
        setRecordingId(null);
        setUserLevel('');
        setAudioUrl('');
        setStableShuffledQuestions([]);
        setExistingQuizDone(false);
        setExistingAudioDone(false);
        setAudioUploadError('');
        // Clear localStorage when quiz is completed
        if (sessionId) {
          localStorage.removeItem(`quizQuestions_${sessionId}`);
          localStorage.removeItem(`quizAnswers_${sessionId}`);
          localStorage.removeItem('quizSessionId');
        }
      }, 3000);

    } catch (error) {
      console.error('Quiz submission error:', error);
      setQuizSubmitted(true);

      setTimeout(() => {
        onClose();
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          purpose: '',
          customPurpose: '',
          collegeName: '',
          departmentName: ''
        });
        setShowQuiz(false);
        setQuizAnswers({});
        setAudio(null);
        setQuizSubmitted(false);
        setRecordingId(null);
        setUserLevel('');
        setAudioUrl('');
        setStableShuffledQuestions([]);
        setExistingQuizDone(false);
        setExistingAudioDone(false);
        setAudioUploadError('');
        // Clear localStorage when quiz is completed
        if (sessionId) {
          localStorage.removeItem(`quizQuestions_${sessionId}`);
          localStorage.removeItem(`quizAnswers_${sessionId}`);
          localStorage.removeItem('quizSessionId');
        }
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBackToForm = () => {
    setShowQuiz(false);
  };

  return (
    <div className='popup-form-overlay-total'>
      <div className="popup-form-overlay">
        {!showQuiz ? (
          // Registration Form with Image and Form Side by Side
          <div className="popup-form-container">
            <button type="button" className="popup-close-btn" onClick={onClose}>
              <span className="close-icon">×</span>
            </button>
            
            {/* Left Side - Image */}
            <div className="popup-form-left">
              <div className="popup-form-image">
                <img src={imgs1} alt="Free trial registration form" />
              </div>
            </div>
            
            {/* Right Side - Form */}
            <div className="popup-form-right">
              <form className="popup-form" onSubmit={handleFormSubmit}>
                <h2>Free Trial Registration</h2>
                
                {registrationError && (
                  <div style={{
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    color: '#c33',
                    padding: '10px',
                    borderRadius: '5px',
                    marginBottom: '15px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span style={{ marginRight: '8px' }}>⚠️</span>
                    {registrationError}
                  </div>
                )}
                
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                />

                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={emailError ? 'error' : ''}
                  style={emailError ? { borderColor: '#c33' } : {}}
                />
{emailError && (
                  <span style={{
                    color: '#c33',
                    fontSize: '12px',
                    marginTop: '5px',
                    display: 'block',
                    fontWeight: 'normal'
                  }}>{emailError}</span>
                )}

                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className={phoneError ? 'error' : ''}
                  style={phoneError ? { borderColor: '#c33' } : {}}
                />
                {phoneError && (
                  <span style={{
                    color: '#c33',
                    fontSize: '12px',
                    marginTop: '5px',
                    display: 'block',
                    fontWeight: 'normal'
                  }}>{phoneError}</span>
                )}

                <input
                  type="text"
                  name="location"
                  placeholder="Your Location"
                  required
                  value={formData.location}
                  onChange={handleChange}
                />

                <input
                  type="text"
                  name="collegeName"
                  placeholder="College Name"
                  value={formData.collegeName}
                  onChange={handleChange}
                />

                <input
                  type="text"
                  name="departmentName"
                  placeholder="Department Name"
                  value={formData.departmentName}
                  onChange={handleChange}
                />

                <select
                  name="purpose"
                  required
                  value={formData.purpose}
                  onChange={handleChange}
                >
                  {purposeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {formData.purpose === 'Others' && (
                  <textarea
                    name="customPurpose"
                    placeholder="Please specify your purpose of study"
                    rows="3"
                    required
                    value={formData.customPurpose}
                    onChange={handleChange}
                  ></textarea>
                )}

                <div className="popup-form-buttons">
                  <button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="popup-submit-btn"
                  >
                    {isSubmitting ? 'Submitting...' : 'Take Assessment →'}
                  </button>
                  <button type="button" onClick={onClose} className="popup-cancel-btn">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : questionsLoading ? (
          // Loading state
          <div className="popup-quiz-loading">
            <button type="button" className="popup-close-btn" onClick={onClose}>
              <span className="close-icon">×</span>
            </button>
            
            <h2>Loading Questions...</h2>
            <p>Please wait while we fetch your assessment questions.</p>
          </div>
        ) : quizSubmitted ? (
          // Quiz Success Message
          <div className="popup-quiz-submitted">
            <button type="button" className="popup-close-btn" onClick={onClose}>
              <span className="close-icon">×</span>
            </button>
            
            <h2>Quiz Submitted Successfully!</h2>
            <p>Thank you {formData.name} for completing the quiz.</p>
            <p>We will contact you soon with your results and next steps.</p>
            {audio && (
              <div>
                <p>Audio recording submitted successfully</p>
                {audioUrl && (
                  <p>
                    <a href={audioUrl} target="_blank" rel="noopener noreferrer">
                      Listen to your recording
                    </a>
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          // Quiz Form
          <form className="popup-quiz-form" onSubmit={handleQuizSubmit}>
            <button type="button" className="popup-close-btn" onClick={onClose}>
              <span className="close-icon">×</span>
            </button>
            
            {existingQuizDone ? (
              <h2>Hello {formData.name}, just one step left — upload your audio recording!</h2>
            ) : existingAudioDone ? (
              <h2>Hello {formData.name}, finish up by answering these questions ({stableShuffledQuestions.length} Questions)</h2>
            ) : (
              <h2>Hello {formData.name}, take this quick quiz! ({stableShuffledQuestions.length} Questions)</h2>
            )}

            {!existingQuizDone && stableShuffledQuestions.map((q, index) => {
              // A reading-comprehension passage is shared by several
              // consecutive sub-questions — show the passage text once,
              // above the first one, but repeat the "Reading Comprehension"
              // heading on every sub-question so it's clear each one still
              // belongs to that section.
              const previous = stableShuffledQuestions[index - 1];
              const isFirstInPassageGroup = q.passage && (!previous || previous.passage_id !== q.passage_id);

              return (
              <div key={q.id} className="popup-question-block">
                {q.passage && (
                  <div className="popup-question-passage">
                    <p className="popup-passage-label">📖 Reading Comprehension</p>
                    {isFirstInPassageGroup && <p>{q.passage}</p>}
                  </div>
                )}
                <p><strong>Q{index + 1}:</strong> {q.text}</p>
                {q.options.map((opt) => (
                  <label key={opt.value}>
                    <input
                      type="radio"
                      name={`q${q.id}`}
                      value={opt.value}
                      checked={quizAnswers[q.id] === opt.value}
                      onChange={() => handleOptionChange(q.id, opt.value)}
                      required
                    />
                    <span>{opt.value}) {opt.label}</span>
                  </label>
                ))}
              </div>
              );
            })}

            {!existingAudioDone && (
              <div className="popup-audio-upload">
                <label><strong>Upload Audio Recording{selectedTopic ? ` (${selectedTopic})` : ''}:</strong></label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioChange}
                />
                {audio && <p>Audio file selected: {audio.name}</p>}
                {audioUploadError && (
                  <span style={{ color: '#c33', fontSize: '12px', display: 'block' }}>
                    {audioUploadError}
                  </span>
                )}
              </div>
            )}

            <div className="popup-form-buttons">
              <button type="submit" disabled={isSubmitting} className="popup-submit-btn">
                {isSubmitting ? 'Submitting Quiz...' : 'Submit Quiz'}
              </button>
              <button type="button" onClick={goBackToForm} className="popup-cancel-btn">
                ← Back to Form
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default PopupForm;
